// Merging the account-level equipment roster with what this browser knows.
//
// Pure on purpose: the surrounding provider is welded to Web Bluetooth, which
// jsdom has no answer for, so the decision-making lives here where it can be
// tested directly.

/**
 * Identity of a physical piece of gear, independent of the Web Bluetooth id
 * (which differs per browser profile for the very same strap).
 */
export const deviceSignature = ({ kind, type, name }) =>
  `${kind}|${(type || "").toLowerCase()}|${String(name || "").trim().toLowerCase()}`;

const toLocal = (row) => ({
  rowId: row.id,
  kind: row.kind,
  id: row.device_key,
  name: row.name,
  type: row.kind === "hrm" ? "hr_monitor" : row.equipment_type,
});

/**
 * Split a server roster into the rows this browser can act on and the rows it
 * can only offer as a re-pair.
 *
 * @param {Object} opts
 * @param {Array}  opts.rows          - rows from GET /api/equipment, newest first
 * @param {string} opts.browserKey    - this browser's key (see browserKey.js)
 * @param {Array}  opts.localDevices  - gear already known here, as
 *                                      `{ kind, name, type }`
 * @returns {{ hydrate: Array, foreign: Array, stale: Array }}
 *   `hydrate` — paired in this browser, so the saved Web Bluetooth id is still
 *   valid and the normal silent-reconnect path can use it.
 *   `foreign` — paired elsewhere on this account. Shown as a named entry the
 *   user can tap to pair here; the id it carries is not usable locally.
 *   `stale` — older rows from this browser for gear it has paired again
 *   since. Chrome hands out a new id on each pick without persistent
 *   permissions, so the old row is the same bike under a dead id (#178).
 */
export function mergeRoster({ rows = [], browserKey, localDevices = [] } = {}) {
  const usable = (rows || []).filter(
    (r) => r && r.device_key && r.name && r.browser_key,
  );

  // Newest first, so the first row for a piece of gear is the live one.
  const hydrate = [];
  const stale = [];
  const mine = new Set();
  for (const row of usable.filter((r) => r.browser_key === browserKey)) {
    const device = toLocal(row);
    const sig = deviceSignature(device);
    if (mine.has(sig)) stale.push(device);
    else {
      mine.add(sig);
      hydrate.push(device);
    }
  }

  // Anything we already have a handle on locally — whether from a live session
  // or from the roster rows we just hydrated — suppresses the foreign copy, so
  // one bike paired on two phones lists once.
  const seen = new Set(localDevices.map(deviceSignature));
  for (const d of hydrate) seen.add(deviceSignature(d));

  const foreign = [];
  for (const row of usable) {
    if (row.browser_key === browserKey) continue;
    const device = toLocal(row);
    const sig = deviceSignature(device);
    if (seen.has(sig)) continue;
    seen.add(sig);
    foreign.push({ ...device, foreign: true });
  }

  return { hydrate, foreign, stale };
}

export default mergeRoster;
